import {
  Box,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState } from "react";

import { fieldSx } from "./sectionStyles";

import {
  isTestDurationValid,
  variationLetter,
  type TestDurationUnit,
  type WinningMetric,
} from "@/features/BroadcastV2/store/variationTypes";

export interface BroadcastAbSettingsSectionProps {
  winningMetric: WinningMetric;
  onWinningMetricChange: (metric: WinningMetric) => void;
  testSizePercent: number;
  onTestSizePercentChange: (percent: number) => void;
  testDurationValue: number;
  onTestDurationValueChange: (value: number) => void;
  testDurationUnit: TestDurationUnit;
  onTestDurationUnitChange: (unit: TestDurationUnit) => void;
  disabled?: boolean;
  /** Hide the "Open Rate" metric (e.g. SMS, which has no opens). */
  hideOpenRate?: boolean;
  /** Number of variation tiles the test size is split evenly across. */
  tileCount: number;
}

/** Formats a percent to one decimal, dropping a trailing ".0" (e.g. 6.5, 7). */
function formatPercent(value: number): string {
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

const WINNING_METRIC_OPTIONS = [
  { value: "open_rate", label: "Open Rate" },
  { value: "click_rate", label: "Click Rate" },
  { value: "placed_order_rate", label: "Placed Order Rate" },
] as const;

/** Winning-metric / test-size / test-duration settings for a multivariate test. */
export function BroadcastAbSettingsSection({
  winningMetric,
  onWinningMetricChange,
  testSizePercent,
  onTestSizePercentChange,
  testDurationValue,
  onTestDurationValueChange,
  testDurationUnit,
  onTestDurationUnitChange,
  disabled,
  hideOpenRate,
  tileCount,
}: BroadcastAbSettingsSectionProps) {
  const metricOptions = hideOpenRate
    ? WINNING_METRIC_OPTIONS.filter(({ value }) => value !== "open_rate")
    : WINNING_METRIC_OPTIONS;

  // If Open Rate is hidden but currently selected (e.g. the default), move to
  // the first available metric so the radio group has a valid selection.
  useEffect(() => {
    if (hideOpenRate && winningMetric === "open_rate" && !disabled) {
      onWinningMetricChange("click_rate");
    }
  }, [hideOpenRate, winningMetric, disabled, onWinningMetricChange]);

  // A controlled number input can't represent an empty string, so mirror the
  // duration value in local string state to allow free typing (incl. clearing
  // the field mid-edit). Sync from the prop only while not focused so external
  // changes land without clobbering an in-progress edit.
  const [durationText, setDurationText] = useState(String(testDurationValue));
  const [durationFocused, setDurationFocused] = useState(false);
  useEffect(() => {
    if (!durationFocused) setDurationText(String(testDurationValue));
  }, [testDurationValue, durationFocused]);

  const durationInvalid =
    !disabled &&
    testSizePercent < 100 &&
    !isTestDurationValid(testDurationValue, testDurationUnit);

  // Build the proportional split-bar slices: one per variant tile, then the
  // winner-send remainder. Variant tints step from solid primary (A) down to a
  // lighter shade so each tile is distinguishable; the winner slice stays faint.
  const perVariantPercent = testSizePercent / tileCount;
  const winnerPercent = 100 - testSizePercent;
  const segments = [
    ...Array.from({ length: tileCount }, (_, i) => ({
      key: `variant-${i}`,
      label: variationLetter(i),
      widthPercent: perVariantPercent,
      bgAlpha: tileCount === 1 ? 1 : 1 - (i / (tileCount - 1)) * 0.5,
      labelColor: "primary.contrastText",
    })),
    ...(winnerPercent > 0
      ? [
          {
            key: "winner",
            label: "Winner",
            widthPercent: winnerPercent,
            bgAlpha: 0.12,
            labelColor: "text.secondary",
          },
        ]
      : []),
  ];

  return (
    <Stack
      gap={6}
      sx={(theme) => ({
        backgroundColor: theme.palette.background["paper-elevation-1"],
        borderRadius: `${theme.shape.borderRadiusMd}px`,
        p: 6,
      })}
    >
      {/* Winning Metric */}
      <Stack gap={1}>
        <Typography variant="body1">Winning Metric</Typography>
        <RadioGroup
          row
          value={winningMetric}
          onChange={(e) =>
            onWinningMetricChange(e.target.value as WinningMetric)
          }
        >
          {metricOptions.map(({ value, label }) => (
            <FormControlLabel
              key={value}
              value={value}
              disabled={disabled}
              label={<Typography variant="body2">{label}</Typography>}
              control={<Radio size="small" />}
            />
          ))}
        </RadioGroup>
      </Stack>

      {/* Test Size */}
      <Stack gap={3}>
        <Typography variant="body1">Test Size</Typography>
        <Typography variant="body2" color="text.secondary">
          Portion of your audience included in the test will be split evenly
          across these tiles. The winner is sent to everyone else.
        </Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <TextField
            value={testSizePercent}
            onChange={(e) => onTestSizePercentChange(Number(e.target.value))}
            type="number"
            size="small"
            disabled={disabled}
            inputProps={{ min: 1, max: 100 }}
            sx={(theme) => ({ width: 100, ...fieldSx(theme) })}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />
          <Slider
            value={testSizePercent}
            onChange={(_, v) => onTestSizePercentChange(v)}
            min={1}
            max={100}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.primary"
            sx={{ minWidth: 40, textAlign: "right" }}
          >
            {testSizePercent}%
          </Typography>
        </Stack>
        {/* One slice per variant (A, B, …) graduating from solid primary to a
            lighter tint, plus a faint "Winner" slice for the remainder. Each
            slice grows purely in proportion to its share (flex-grow on a zero
            basis, minWidth 0) so it never resizes to fit its label; labels are
            hidden on slices too narrow to read. */}
        <Box
          sx={(theme) => ({
            display: "flex",
            minHeight: theme.spacing(8),
            borderRadius: `${theme.shape.borderRadiusMd}px`,
            overflow: "hidden",
          })}
        >
          {segments.map((seg) => (
            <Box
              key={seg.key}
              sx={(theme) => ({
                flex: `${seg.widthPercent} 0 0`,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                backgroundColor: alpha(theme.palette.primary.main, seg.bgAlpha),
              })}
            >
              {seg.widthPercent >= 8 && (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={seg.labelColor}
                  noWrap
                >
                  {seg.label}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {formatPercent(perVariantPercent)}% per variant
          {winnerPercent > 0 && ` • ${winnerPercent}% winner send`}
        </Typography>
      </Stack>

      {/* Test Duration — irrelevant at 100% test size (no winner send). */}
      {testSizePercent < 100 && (
        <Stack gap={2}>
          <Typography variant="body1">Test Duration</Typography>
          <Stack direction="row" gap={2} alignItems="center">
            <TextField
              value={durationText}
              onFocus={() => setDurationFocused(true)}
              onChange={(e) => {
                const raw = e.target.value;
                setDurationText(raw);
                const num = Number(raw);
                if (raw !== "" && Number.isFinite(num)) {
                  onTestDurationValueChange(num);
                }
              }}
              onBlur={() => {
                setDurationFocused(false);
                // Restore an empty/invalid field to the last committed value.
                if (
                  durationText === "" ||
                  !Number.isFinite(Number(durationText))
                ) {
                  setDurationText(String(testDurationValue));
                }
              }}
              type="number"
              size="small"
              disabled={disabled}
              error={durationInvalid}
              inputProps={{
                min: testDurationUnit === "Hours" ? 4 : 1,
              }}
              sx={(theme) => ({ width: 80, ...fieldSx(theme) })}
            />
            <Select
              value={testDurationUnit}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "Hours" || val === "Days") {
                  onTestDurationUnitChange(val);
                }
              }}
              size="small"
              disabled={disabled}
              sx={(theme) => ({
                backgroundColor: "common.white",
                borderRadius: `${theme.shape.borderRadiusMd}px`,
              })}
            >
              <MenuItem value="Hours">Hours</MenuItem>
              <MenuItem value="Days">Days</MenuItem>
            </Select>
          </Stack>
          {durationInvalid && (
            <Typography variant="caption" color="error">
              Minimum duration is 4 hours.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            After the test ends, Queen One automatically sends the winner to
            remaining recipients.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
